/**
 * Staff API wrapper.
 *
 * **v2 staff entity (DNKADM-0053, handoff §5.6).** Staff are now a FIRST-CLASS
 * `v2_staff` entity — replacing the v1 `lastName == "ServiceAccount"` partition of
 * staff vs resources (§9.1/§9.3). This module owns the v2 entity CRUD +
 * soft-delete/reactivate (§4.3), per-(staff, location) weekly schedules, date
 * exceptions, appointment caps (§5.6), location assignments, and the fail-closed
 * `useStaffPickList()` the service form (0055) + coach assignment (0059) consume.
 * Bearer-only `/v2/admin/**` surface — `request()` routes it (DNKADM-0049
 * `isV2Surface`): bearer token, NO CSRF, NO cookies, the bounded 401→refresh→retry;
 * the mutating-audit `X-Session-Mode` stamp fires on the writes (`isV2AdminSurface`).
 *
 * **Endpoints (handoff §5.6, §3.1 — standard REST + soft-delete):**
 *  - `GET    /v2/admin/staff?status=active|inactive|all` → {@link V2Staff}`[]`.
 *  - `GET    /v2/admin/staff/{id}` → one {@link V2Staff}.
 *  - `POST   /v2/admin/staff` ← {@link StaffInput} → the created row.
 *  - `PUT    /v2/admin/staff/{id}` ← {@link StaffInput} → the updated row.
 *  - `POST   /v2/admin/staff/{id}/deactivate` | `/reactivate` (§4.3).
 *  - `DELETE /v2/admin/staff/{id}` → Remove (archive, DNKAPI-0697 / DNKADM-0481): `204`,
 *    or `409 STAFF_HAS_UPCOMING_BOOKINGS` naming the bookings in the way (nothing removed).
 *  - `POST   /v2/admin/staff/{id}/restore` → the restored row (it comes back INACTIVE).
 *  - `GET    /v2/admin/staff?removed=true` → removed staff only; the default list
 *    excludes them.
 *  - `GET/PUT /v2/admin/staff/{id}/location-assignments` ← `{ locationIds: [...] }`.
 *  - `GET/PUT /v2/admin/staff/{id}/schedule?locationId=…` ← `{ days: [...] }` (per
 *    location — a staffer at two locations has two schedules; times are the
 *    location's local time).
 *  - `GET/POST/DELETE /v2/admin/staff/{id}/date-exceptions`.
 *  - `GET /v2/admin/staff/{id}/appointment-caps`, `PUT …/appointment-caps/{period}`,
 *    `DELETE …/appointment-caps/{period}`.
 *  - The staff-calendar picker source and the calendar test (DNKADM-0384, DNKAPI-0538
 *    U2/U3) live in `./staffCalendar` — `GET …/{id}/calendars`, `POST/DELETE
 *    …/{id}/calendar-test`. The per-coach routing fields themselves (`calendarId`,
 *    `calendarSyncEnabled`) ride the entity GET/PUT here.
 *  - `GET /v2/me/staff` → {@link getMyStaff} (DNKADM-0363 / DNKAPI-0520, handoff §5):
 *    the SIGNED-IN staff session's own coach row. It is a RESOLVER, not a view: it
 *    answers *which coach am I*, and My schedule composes the rest from the
 *    `/v2/admin/staff/{id}/**` reads above, all of which §2 opens to STAFF.
 *  - `PUT /v2/me/staff/schedule?locationId=…` → {@link putMyStaffSchedule} (DNKADM-0490 /
 *    DNKAPI-0708): the signed-in coach saves their OWN weekly hours, when the tenant's
 *    "Coaches can set their own hours" switch is on.
 *
 *    **This is the app's ONLY `/v2/me/**` read** — the first and, per the
 *    DNKADM-0356 audit, the only one (DNKADM-0490 adds the one `/v2/me/staff/schedule`
 *    write, {@link putMyStaffSchedule}, under the same path). The similar-looking `POST /me/verify-password`
 *    (`api/verifyPassword.ts`, the Front Desk exit re-auth) is the **v1 `/me/**`
 *    surface, a DIFFERENT predicate** (`client.ts` → `isMeSurface`, not
 *    `isV2Surface`), so it is not a `/v2/me` consumer and never was. Both do ride the
 *    same `isBearerSurface`, which is why this needs no new transport.
 *
 * **`v2_staff` field set (handoff §5.6, amended by DNKAPI-0520 / handoff §5):**
 * `firstName`, `lastName`, `email` (opt), `phone` (opt), `authUserId`
 * (`number | null` — the staff LOGIN this coach record belongs to), `isActive`.
 * Unique on `(tenant, firstName, lastName)` — the create/update `409` surfaces inline.
 *
 * **The old CRM-contact id is RETIRED here (DNKADM-0362).** A coach is staff, and
 * staff-are-never-in-the-CRM is the customer/staff split's enforcement, so that field
 * is gone from both the response and the request: the server ignores it on the way in
 * and stops sending it on the way out. The similar-looking contact link on the
 * saas-auth users roster (`features/members/AddMemberDialog`) is a DIFFERENT thing —
 * a CUSTOMER's CRM link — and is untouched by this.
 *
 * **`dayOfWeek` is 0=Sun … 6=Sat** here (the §5.6 staff/resource convention — note it
 * DIFFERS from the v1 operating-hours 0=Mon; the editor renders Sun→Sat). Shift
 * validation (`@/utils/shiftValidation`) mirrors the server: `start < end`, no
 * overlaps/duplicates within a day/date/scope, overnight disallowed.
 *
 * **Sub-resource wire shapes are ASSUMED standard REST** (handoff §10 — the live
 * OpenAPI "v2" group is authoritative when available; recorded in the DNKADM-0053
 * delivery's Notes). Every read is parsed DEFENSIVELY (mirrors `locations.ts`): a
 * missing/wrong-typed field degrades (an entry with no usable `id`/key is dropped),
 * never a throw.
 *
 * The Reservations Calendar also derives its staff lanes from {@link listStaff};
 * there is no separate legacy roster read. The v1 coach-assignment helpers —
 * `getStaff` / `assignStaff` / `unassignStaff` — were removed in DNKADM-0074 as dead
 * v1 cutover residue.
 */
import { request } from './client';
import { useCallback } from 'react';
import { usePickList, type PickList } from '@/hooks/usePickList';
import { ApiError } from './errors';
import { v2ErrorCode } from './v2Errors';
import { PickListError } from './picklists';
import { parseRowLocations, type LocationSummary } from './locations';
import { parseBookingConflict, type BookingConflict } from './reservations';
import type { ActiveFilterValue } from '@/hooks/useActiveFilter';
import type { ShiftLike } from '@/utils/shiftValidation';

/** v2 admin staff base path (bearer surface). */
const PATH = '/v2/admin/staff';

/**
 * The signed-in staff session's own coach row (DNKAPI-0520, handoff §5) — the ONLY
 * `/v2/me/**` path this app reads. Same bearer surface as `/v2/admin/**` (`client.ts`
 * → `isV2Surface`): bearer token, no CSRF, no cookies, the same bounded
 * 401→refresh→retry. No new transport.
 */
const ME_STAFF_PATH = '/v2/me/staff';

/** The seven days of the week, `dayOfWeek` 0=Sunday … 6=Saturday (§5.6 convention). */
const STAFF_DAYS_OF_WEEK = [0, 1, 2, 3, 4, 5, 6] as const;

/**
 * The setup-health codes (DNKAPI-0714 / DNKADM-0492), in the fixed order the server
 * sends them. The first three block "bookable"; the last two are warnings only.
 */
export const STAFF_SETUP_ITEMS = [
  'NO_LOCATION',
  'NO_HOURS',
  'NO_SERVICES',
  'CALENDAR_NOT_LINKED',
  'LOGIN_NOT_LINKED',
] as const;
export type StaffSetupItem = (typeof STAFF_SETUP_ITEMS)[number];

/**
 * What a coach still needs before members can book them (DNKAPI-0714), computed by
 * dink-api on every read. `bookable` is false for an inactive or removed coach even
 * when `missing` is empty. `missing` holds only the codes this client knows, in the
 * server's order.
 */
export interface StaffSetupHealth {
  bookable: boolean;
  missing: StaffSetupItem[];
}

/** The three cap periods (calendar; week resets Sunday, §5.6). */
export const CAP_PERIODS = ['day', 'week', 'month'] as const;
/** One appointment-cap period. */
export type CapPeriod = (typeof CAP_PERIODS)[number];

/**
 * The six reservation status wire values (handoff §4.8) — the DEFAULT `countedStatuses`
 * of a new appointment cap (a hard throughput ceiling counts every status). Order is
 * the §4.8 order.
 */
export const RESERVATION_STATUSES = [
  'new',
  'confirmed',
  'cancelled',
  'showed',
  'no_show',
  'invalid',
] as const;
/** One reservation status (the cap's `countedStatuses` element). */
export type ReservationStatus = (typeof RESERVATION_STATUSES)[number];

/**
 * One `v2_staff` row (§5.6). `email`/`phone` are optional strings (absent when the
 * server carries none — `exactOptionalPropertyTypes`: the key is omitted, never
 * `undefined`).
 */
export interface V2Staff {
  id: string;
  firstName: string;
  lastName: string;
  /** Soft-delete flag (§4.3); `false` = deactivated. */
  isActive: boolean;
  email?: string;
  phone?: string;
  /**
   * The **staff login** this coach record belongs to (DNKAPI-0520 / handoff §5): the
   * `userId` of a Team & access roster row with `kind: "STAFF"`. `null` when the coach
   * has no login linked (and for a legacy body that omits the field). ALWAYS present
   * as a key so "absent" and "explicitly unlinked" read the same to every consumer —
   * this is the link a STAFF session follows to find its own coach record.
   * Never rendered as a raw number: resolve it against the roster to `name (email)`.
   */
  authUserId: number | null;
  /**
   * The coach's staff-calendar routing (DNKADM-0384 U2, DNKAPI-0538 U1): the opaque
   * provider calendar id their bookings mirror onto — `null` = none chosen — and
   * whether that mirroring is on. The id is DATA for matching the picker's selection
   * and for the write-back; it is NEVER rendered (the picker shows names only). A
   * paused coach keeps their id, so re-enabling needs no re-selection. Both keys are
   * always present; a legacy body missing them reads as "none / off".
   */
  calendarId: string | null;
  calendarSyncEnabled: boolean;
  /**
   * The owned location(s) this staff member is assigned to (DNKADM-0078), carried
   * INLINE on the list row (`HANDOFF-dink-api-list-row-locations`) so the list
   * screen renders an assigned-Locations column with NO per-row fan-out. Absent
   * (`undefined`) = unknown / field not emitted yet (the column shows "—"); an
   * explicit `[]` = assigned to no locations (the column shows red "No locations").
   */
  locations?: LocationSummary[];
  /**
   * When this staffer was removed (archived — DNKAPI-0697 / DNKADM-0481), as the wire's
   * ISO instant. Present only on a removed row (the `?removed=true` list); a live row
   * omits the key (`exactOptionalPropertyTypes` — absent, never `undefined`), so "not
   * removed" needs no explicit `null` in every fixture.
   */
  removedAt?: string;
  /**
   * Setup health (DNKAPI-0714 / DNKADM-0492). Absent when the server did not send a
   * usable object (an older dink-api): every consumer then shows nothing, never a guess.
   */
  setupHealth?: StaffSetupHealth;
  /**
   * The services this coach teaches (DNKAPI-0715 / DNKADM-0498): their active service
   * links, removed services left out. Sent ONLY on the single staff GET
   * (`GET /v2/admin/staff/{id}`); the list, `/v2/me/staff` and the write responses omit the
   * key, and so does an older dink-api. Absent (`undefined`) = not sent; `[]` = none.
   */
  services?: StaffServiceSummary[];
}

/**
 * One service a coach teaches (DNKAPI-0715). `status` is the service's lifecycle status as
 * the server sends it (`ACTIVE`, `DRAFT`, ...), kept as a string.
 */
export interface StaffServiceSummary {
  id: string;
  name: string;
  status: string;
}

/**
 * Create/update body for a staff member. `firstName`/`lastName` are required by the
 * form; the rest are optional. `isActive` is NOT settable here — activation is the
 * dedicated deactivate/reactivate endpoints (§4.3).
 */
export interface StaffInput {
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  /**
   * The staff login to link (DNKAPI-0520 / handoff §5): a **number** links that
   * roster `userId`, an explicit **`null`** clears the link. **On update, ALWAYS send
   * it** with the record's current value (DNKADM-0392): dink-api applies
   * `authUserId` from the request unconditionally, and Jackson binds an absent key
   * and an explicit `null` identically — so an update body WITHOUT the key clears the
   * link. The key is optional here only for a create with no login chosen (there is
   * nothing to clear on a new row). Not the null-means-unchanged shape of the two
   * calendar fields below.
   */
  authUserId?: number | null;
  /**
   * The provider calendar to route this coach's bookings onto (DNKADM-0384 U2,
   * DNKAPI-0538 U1). Omitted = unchanged (the server keeps what it has; a body that
   * does not carry the key can never wipe a mapping). The console only ever SETS an id
   * the picker offered — it never types one and never sends the server's `""` clear.
   */
  calendarId?: string;
  /**
   * The per-coach sync switch (DNKAPI-0538 U1). Omitted = unchanged. The server
   * rejects the post-apply state "on with no calendar" with
   * `400 STAFF_CALENDAR_SYNC_NEEDS_CALENDAR`; the form never offers that state.
   * Turning it off keeps the calendar id.
   */
  calendarSyncEnabled?: boolean;
}

/** Options for {@link listStaff}. */
export interface ListStaffOptions {
  /** Active/Inactive/All filter (§4.3); defaults to `active` (live rows only). */
  status?: ActiveFilterValue;
  /**
   * Convenience flag for {@link listStaff} (mirrors the spec's
   * `listStaff({ includeInactive? })`): `true` ⇒ `status=all`. Ignored when an
   * explicit `status` is also passed.
   */
  includeInactive?: boolean;
  /**
   * `true` ⇒ the removed (archived) staff ONLY — `?removed=true`, with no `status`
   * (removed staff are inactive by construction, so a status filter would only hide
   * them). DNKAPI-0697 / DNKADM-0481. Overrides `status`/`includeInactive`.
   */
  removed?: boolean;
}

/** The minimal `{ id, name }` option {@link useStaffPickList} returns. */
export interface StaffOption {
  id: string;
  name: string;
  isActive: boolean;
}

/** One shift within a schedule day / exception (`"HH:mm"`, local). */
export type StaffShift = ShiftLike;

/**
 * One day of a per-(staff, location) weekly schedule. `dayOfWeek` 0=Sun … 6=Sat.
 * When `isAvailable` is false the day is off and carries no shifts.
 */
export interface StaffScheduleDay {
  dayOfWeek: number;
  isAvailable: boolean;
  shifts: StaffShift[];
}

/** A parsed `GET …/schedule` — the seven days in `dayOfWeek` 0→6 order. */
export interface StaffSchedule {
  locationId: string | null;
  days: StaffScheduleDay[];
}

/** A date exception's scope (§5.6): all locations, or one location. */
export type ExceptionScope = 'ALL' | 'LOCATION';

/**
 * One staff date exception (§5.6). `exceptionDate` is a local `YYYY-MM-DD`;
 * `isAvailable` false = off that date. `scope` is `ALL` (every location) or
 * `LOCATION` (one location, `locationId` set). When available, optional override
 * `shifts`. `id` is the server row id (present on reads; used to DELETE).
 */
export interface StaffDateException {
  id: string | null;
  exceptionDate: string;
  isAvailable: boolean;
  scope: ExceptionScope;
  /** The scoped location id when `scope === 'LOCATION'`; null/absent for `ALL`. */
  locationId: string | null;
  shifts: StaffShift[];
}

/** Create body for a date exception (no `id` — the server assigns it). */
export interface StaffDateExceptionInput {
  exceptionDate: string;
  isAvailable: boolean;
  scope: ExceptionScope;
  /** Required when `scope === 'LOCATION'`; omitted for `ALL`. */
  locationId?: string;
  shifts: StaffShift[];
}

/**
 * One appointment cap (§5.6) for a `(staff, period)`. `maxAppointments` is the
 * ceiling; `countedStatuses` is which reservation statuses count toward it (default
 * all six). At most one cap per `(staff, period)`.
 */
export interface StaffAppointmentCap {
  period: CapPeriod;
  maxAppointments: number;
  countedStatuses: ReservationStatus[];
}

/** Upsert body for an appointment cap. */
export interface StaffAppointmentCapInput {
  maxAppointments: number;
  countedStatuses: ReservationStatus[];
}

// --- Defensive parsing helpers (mirrors locations.ts) --------------------------

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** A trimmed non-empty string, or null. */
function asText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/**
 * A stable string id from a NUMBER or a string (DNKADM-0064): v2 entity ids arrive
 * as numbers on the wire and are normalized to the `string` the UI/router use — a
 * finite number → `String(n)`, a non-empty string → trimmed, else null. Preserves
 * genuinely-string ids (accepts both); a numeric id is never dropped.
 */
function asId(value: unknown): string | null {
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : null;
  return asText(value);
}

/** A boolean from a real boolean, or a fallback when absent/unparseable. */
function asBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

/** A `"HH:mm"` time string (24h), or null. */
function asTime(value: unknown): string | null {
  const text = asText(value);
  if (text === null) return null;
  return /^([01]\d|2[0-3]):[0-5]\d$/.test(text) ? text : null;
}

/** A `YYYY-MM-DD` date string, or null. */
function asDate(value: unknown): string | null {
  const text = asText(value);
  if (text === null) return null;
  return /^\d{4}-\d{2}-\d{2}$/.test(text) ? text : null;
}

/** A `dayOfWeek` index in 0–6 (number or numeric string), or null. */
function asDayOfWeek(value: unknown): number | null {
  let n: number | null = null;
  if (typeof value === 'number' && Number.isInteger(value)) n = value;
  else if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    if (Number.isInteger(parsed)) n = parsed;
  }
  if (n === null || n < 0 || n > 6) return null;
  return n;
}

/** A non-negative integer, or null. */
function asNonNegInt(value: unknown): number | null {
  if (typeof value === 'number' && Number.isInteger(value) && value >= 0) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    if (Number.isInteger(parsed) && parsed >= 0) return parsed;
  }
  return null;
}

/**
 * Assign an optional text field onto `target` only when it parses to a non-empty
 * string — keeps the optional key absent (not `undefined`) per
 * `exactOptionalPropertyTypes`.
 */
function assignOptionalText(
  target: Record<string, unknown>,
  key: string,
  value: unknown,
): void {
  const text = asText(value);
  if (text !== null) target[key] = text;
}

/**
 * Parse `setupHealth` defensively: `undefined` unless it is an object with a real
 * boolean `bookable` and an array `missing`. Unknown codes are dropped and the known
 * ones keep the server's fixed order.
 */
function parseSetupHealth(value: unknown): StaffSetupHealth | undefined {
  if (!isObject(value)) return undefined;
  const bookable = value['bookable'];
  const missing = value['missing'];
  if (typeof bookable !== 'boolean' || !Array.isArray(missing)) return undefined;
  return {
    bookable,
    missing: STAFF_SETUP_ITEMS.filter((item) => missing.includes(item)),
  };
}

/**
 * Parse one raw staff row, or null when it has no usable `id` (a half-identified
 * entry is dropped rather than surfaced as a selectable option). A missing
 * `firstName`/`lastName` degrades to an empty string (the row still renders; the
 * server is the authority).
 */
function parseStaff(raw: unknown): V2Staff | null {
  if (!isObject(raw)) return null;
  const id = asId(raw['id']);
  if (id === null) return null;

  const acc: Record<string, unknown> = {};
  assignOptionalText(acc, 'email', raw['email']);
  assignOptionalText(acc, 'phone', raw['phone']);
  // Removal stamp (DNKAPI-0697): a non-empty string only; `null`/absent leaves the key off.
  assignOptionalText(acc, 'removedAt', raw['removedAt']);
  const staff: V2Staff = {
    id,
    firstName: asText(raw['firstName']) ?? '',
    lastName: asText(raw['lastName']) ?? '',
    // The wire flag is `active` (DNKADM-0064); tolerate legacy `isActive`.
    isActive: asBoolean(raw['active'] ?? raw['isActive'], true),
    // The staff-login link (DNKAPI-0520). A number links; `null`/absent/unparseable is
    // "not linked". A legacy body still carrying the retired CRM-contact id parses fine:
    // the field is simply not read, so it never reaches the model.
    authUserId: asNonNegInt(raw['authUserId']),
    // Staff-calendar routing (DNKAPI-0538 U1): an opaque id or none; the flag defaults
    // OFF when absent/unparseable — a legacy body can never read as "syncing".
    calendarId: asText(raw['calendarId']),
    calendarSyncEnabled: asBoolean(raw['calendarSyncEnabled'], false),
    ...(acc as Partial<V2Staff>),
  };
  // Inline assigned-locations summary (DNKADM-0078) — assigned only when the field
  // is present (absent ⇒ key omitted, exactOptionalPropertyTypes; absent ≠ empty).
  const locations = parseRowLocations(raw);
  if (locations !== undefined) staff.locations = locations;
  // Setup health (DNKAPI-0714): assigned only when usable (absent ⇒ key omitted).
  const setupHealth = parseSetupHealth(raw['setupHealth']);
  if (setupHealth !== undefined) staff.setupHealth = setupHealth;
  // The coach's services (DNKAPI-0715): assigned only when the key is an array.
  const services = parseStaffServices(raw['services']);
  if (services !== undefined) staff.services = services;
  return staff;
}

/**
 * Parse the single staff GET's `services` defensively: `undefined` unless it is an array.
 * An entry with no usable `id` is dropped; a missing `name` degrades to the id and a
 * missing `status` to an empty string.
 */
function parseStaffServices(value: unknown): StaffServiceSummary[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const services: StaffServiceSummary[] = [];
  for (const entry of value) {
    if (!isObject(entry)) continue;
    const id = asId(entry['id']);
    if (id === null) continue;
    services.push({
      id,
      name: asText(entry['name']) ?? id,
      status: asText(entry['status']) ?? '',
    });
  }
  return services;
}

/**
 * Extract the staff array TOLERANTLY from a successful body, returning `null` when
 * NEITHER a bare array NOR a known wrapper key (`staff`/`content`) yields one. The
 * list read turns `null` into `[]` (a real first-run state, §8); the fail-closed
 * pick-list turns it into a {@link PickListError}.
 */
function extractStaff(body: unknown): unknown[] | null {
  if (Array.isArray(body)) return body;
  if (isObject(body)) {
    if (Array.isArray(body['staff'])) return body['staff'];
    if (Array.isArray(body['content'])) return body['content'];
  }
  return null;
}

/**
 * Build the JSON body for a create/update, omitting absent optional keys.
 * `authUserId` is deliberately `!== undefined` (not a truthiness check): an explicit
 * `null` MUST reach the wire — on the server an absent key and `null` both clear the
 * staff-login link (DNKAPI-0520), so every update caller sets it (DNKADM-0392).
 */
function toStaffBody(input: StaffInput): Record<string, unknown> {
  const json: Record<string, unknown> = {
    firstName: input.firstName,
    lastName: input.lastName,
  };
  if (input.email !== undefined) json['email'] = input.email;
  if (input.phone !== undefined) json['phone'] = input.phone;
  if (input.authUserId !== undefined) json['authUserId'] = input.authUserId;
  // Staff-calendar routing (DNKADM-0384 U2): each key goes out only when the caller
  // set it — an omitted key is "unchanged" on the server (DNKAPI-0538 U1).
  if (input.calendarId !== undefined) json['calendarId'] = input.calendarId;
  if (input.calendarSyncEnabled !== undefined) {
    json['calendarSyncEnabled'] = input.calendarSyncEnabled;
  }
  return json;
}

// --- Defensive parsing: schedule / exceptions / caps ---------------------------

/** Parse a raw shifts array, dropping any entry without a valid `"HH:mm"` start AND end. */
function parseShifts(raw: unknown): StaffShift[] {
  if (!Array.isArray(raw)) return [];
  const shifts: StaffShift[] = [];
  for (const entry of raw) {
    if (!isObject(entry)) continue;
    const startTime = asTime(entry['startTime']);
    const endTime = asTime(entry['endTime']);
    if (startTime !== null && endTime !== null) shifts.push({ startTime, endTime });
  }
  return shifts;
}

/**
 * Parse one raw schedule day keyed by its `dayOfWeek`, or null without a usable
 * `dayOfWeek`. A day is unavailable when explicitly closed OR it carries no valid
 * shifts; an unavailable day carries no shifts.
 */
function parseScheduleDay(raw: unknown): { dayOfWeek: number; day: StaffScheduleDay } | null {
  if (!isObject(raw)) return null;
  const dayOfWeek = asDayOfWeek(raw['dayOfWeek']);
  if (dayOfWeek === null) return null;
  const shifts = parseShifts(raw['shifts']);
  const isAvailable = asBoolean(raw['isAvailable'], false) && shifts.length > 0;
  return {
    dayOfWeek,
    day: isAvailable
      ? { dayOfWeek, isAvailable: true, shifts }
      : { dayOfWeek, isAvailable: false, shifts: [] },
  };
}

/** An unavailable-by-default day for a `dayOfWeek` the server didn't return. */
function defaultUnavailableDay(dayOfWeek: number): StaffScheduleDay {
  return { dayOfWeek, isAvailable: false, shifts: [] };
}

/** Normalize a raw `days` payload to exactly the seven days in `dayOfWeek` 0→6 order. */
function parseScheduleDays(raw: unknown): StaffScheduleDay[] {
  const byDay = new Map<number, StaffScheduleDay>();
  if (Array.isArray(raw)) {
    for (const entry of raw) {
      const parsed = parseScheduleDay(entry);
      if (parsed !== null) byDay.set(parsed.dayOfWeek, parsed.day);
    }
  }
  return STAFF_DAYS_OF_WEEK.map((d) => byDay.get(d) ?? defaultUnavailableDay(d));
}

function parseSchedule(raw: unknown): StaffSchedule {
  const o = isObject(raw) ? raw : {};
  return { locationId: asId(o['locationId']), days: parseScheduleDays(o['days']) };
}

/** Parse a raw exception scope; anything but `LOCATION` (case-insensitively) is `ALL`. */
function parseScope(value: unknown): ExceptionScope {
  return typeof value === 'string' && value.toUpperCase() === 'LOCATION' ? 'LOCATION' : 'ALL';
}

/** Parse one raw date exception, or null without a usable `exceptionDate`. */
function parseException(raw: unknown): StaffDateException | null {
  if (!isObject(raw)) return null;
  const exceptionDate = asDate(raw['exceptionDate']);
  if (exceptionDate === null) return null;
  // DNKADM-0388 U1: the response key is `available` (dink-api's DateExceptionResponse);
  // `isAvailable` is read only as a fallback when `available` is absent.
  const isAvailable = asBoolean(raw['available'] ?? raw['isAvailable'], false);
  const scope = parseScope(raw['scope']);
  return {
    id: asId(raw['id']),
    exceptionDate,
    isAvailable,
    scope,
    locationId: scope === 'LOCATION' ? asId(raw['locationId']) : null,
    shifts: isAvailable ? parseShifts(raw['shifts']) : [],
  };
}

/** Tolerantly extract an exceptions array (bare array or `{ exceptions }`/`{ content }`). */
function extractList(body: unknown, key: string): unknown[] {
  if (Array.isArray(body)) return body;
  if (isObject(body)) {
    if (Array.isArray(body[key])) return body[key] as unknown[];
    if (Array.isArray(body['content'])) return body['content'];
  }
  return [];
}

/** Parse one raw `countedStatuses` array into known statuses (unknown values dropped). */
function parseCountedStatuses(raw: unknown): ReservationStatus[] {
  if (!Array.isArray(raw)) return [];
  const out: ReservationStatus[] = [];
  for (const entry of raw) {
    if (
      typeof entry === 'string' &&
      (RESERVATION_STATUSES as readonly string[]).includes(entry) &&
      !out.includes(entry as ReservationStatus)
    ) {
      out.push(entry as ReservationStatus);
    }
  }
  return out;
}

/** A valid cap period, or null. */
function parsePeriod(value: unknown): CapPeriod | null {
  return typeof value === 'string' && (CAP_PERIODS as readonly string[]).includes(value)
    ? (value as CapPeriod)
    : null;
}

/**
 * Parse one raw appointment cap, or null without a usable `period`/`maxAppointments`.
 * An absent/empty `countedStatuses` degrades to ALL six (the documented default,
 * §5.6) so an under-specified row still reflects the intended ceiling.
 */
function parseCap(raw: unknown): StaffAppointmentCap | null {
  if (!isObject(raw)) return null;
  const period = parsePeriod(raw['period']);
  const maxAppointments = asNonNegInt(raw['maxAppointments']);
  if (period === null || maxAppointments === null) return null;
  const counted = parseCountedStatuses(raw['countedStatuses']);
  return {
    period,
    maxAppointments,
    countedStatuses: counted.length > 0 ? counted : [...RESERVATION_STATUSES],
  };
}

// --- Serialization helpers -----------------------------------------------------

function shiftsToBody(shifts: StaffShift[]): Array<{ startTime: string; endTime: string }> {
  return shifts.map((s) => ({ startTime: s.startTime, endTime: s.endTime }));
}

function scheduleDayToBody(day: StaffScheduleDay): Record<string, unknown> {
  return day.isAvailable
    ? { dayOfWeek: day.dayOfWeek, isAvailable: true, shifts: shiftsToBody(day.shifts) }
    : { dayOfWeek: day.dayOfWeek, isAvailable: false, shifts: [] };
}

function exceptionToBody(ex: StaffDateExceptionInput): Record<string, unknown> {
  // DNKADM-0388 U1: the wire key is `available` — dink-api's date-exception request
  // record (`v2/staff/DateExceptionRequest.available`) has NO `@JsonProperty`, unlike the
  // WEEKLY `ScheduleDayDto`, which is pinned to `isAvailable` (DNKAPI-0162). Sending
  // `isAvailable` here was silently ignored and every exception saved as closed.
  const json: Record<string, unknown> = {
    exceptionDate: ex.exceptionDate,
    available: ex.isAvailable,
    scope: ex.scope,
    shifts: ex.isAvailable ? shiftsToBody(ex.shifts) : [],
  };
  if (ex.scope === 'LOCATION' && ex.locationId !== undefined) {
    json['locationId'] = ex.locationId;
  }
  return json;
}

// --- Path helpers --------------------------------------------------------------

function staffPath(id: string): string {
  return `${PATH}/${encodeURIComponent(id)}`;
}

// --- Entity endpoints (DNKADM-0053 U1) -----------------------------------------

/**
 * `GET /v2/admin/staff?status=…` → the tenant's staff, defensively parsed (a bad
 * entry dropped). The `status` filter (§4.3) defaults to `active` (live rows only);
 * pass `status: 'inactive'|'all'` or `includeInactive: true` to include deactivated
 * rows. Throws a typed `ApiError` on a non-2xx response.
 */
export async function listStaff(options: ListStaffOptions = {}): Promise<V2Staff[]> {
  const status = options.status ?? (options.includeInactive ? 'all' : 'active');
  const query = options.removed === true ? { removed: 'true' } : { status };
  const body = await request<unknown>('GET', PATH, { query });
  return (extractStaff(body) ?? [])
    .map(parseStaff)
    .filter((s): s is V2Staff => s !== null);
}

/**
 * `GET /v2/admin/staff/{id}` → one staff member (defensively parsed). Throws a typed
 * `ApiError` (`404` NOT_FOUND, upstream forwarded).
 */
export async function getStaffMember(id: string): Promise<V2Staff | null> {
  const body = await request<unknown>('GET', staffPath(id));
  return parseStaff(body);
}

/**
 * The resolver's "this login has no coach record" answer — `404 STAFF_NOT_LINKED`
 * (DNKADM-0363). A distinct RESULT rather than a thrown error because it is an
 * ordinary, expected state of a correctly-configured tenant (a staff login an owner
 * has not linked yet), which the screen renders as one empty state with no retry.
 */
export interface StaffNotLinked {
  kind: 'not-linked';
}

/** What {@link getMyStaff} resolves to: the caller's own coach row, or "not linked". */
export type MyStaffResult = V2Staff | StaffNotLinked;

/** Narrow a {@link MyStaffResult} to the not-linked answer (`V2Staff` carries no `kind`). */
export function isStaffNotLinked(result: MyStaffResult): result is StaffNotLinked {
  return 'kind' in result;
}

/**
 * `GET /v2/me/staff` → the SIGNED-IN staff session's own coach row (DNKAPI-0520 /
 * handoff §5) — *which coach am I*, the id My schedule composes its reads from.
 *
 * Three answers, three shapes:
 *  - **200** → the parsed {@link V2Staff} (the same defensive parse the admin reads use);
 *  - **`404 STAFF_NOT_LINKED`** → `{ kind: 'not-linked' }`, an ordinary state, not an error;
 *  - **anything else** — `403 NOT_TENANT_STAFF` for a non-staff session (a platform-support
 *    token holds no roster row by construction) included — throws the typed `ApiError`,
 *    which the screen maps to its forbidden / retryable-error state.
 *
 * The 404 branch is matched on the envelope code, not the bare status: any OTHER 404 on
 * this fixed, id-less path is a deployment/routing fault, and reporting it as "you are
 * not linked" would tell every operator in the tenant something false.
 *
 * A `200` whose body carries no usable `id` throws too — the resolver answered, but not
 * with a coach record, and the screen's error+retry state says that honestly rather than
 * degrading a broken response into "not linked".
 */
export async function getMyStaff(): Promise<MyStaffResult> {
  let body: unknown;
  try {
    body = await request<unknown>('GET', ME_STAFF_PATH);
  } catch (err) {
    if (err instanceof ApiError && err.status === 404 && v2ErrorCode(err) === 'STAFF_NOT_LINKED') {
      return { kind: 'not-linked' };
    }
    throw err;
  }
  const staff = parseStaff(body);
  if (staff === null) throw new ApiError('UNKNOWN', 200);
  return staff;
}

/**
 * `POST /v2/admin/staff` → create a staff member. Returns the re-parsed created row.
 * Throws a typed `ApiError` (`400` VALIDATION inline-able, `409` CONFLICT on the
 * unique `(tenant, firstName, lastName)` rule, upstream).
 */
export async function createStaff(input: StaffInput): Promise<V2Staff | null> {
  const body = await request<unknown>('POST', PATH, { json: toStaffBody(input) });
  return parseStaff(body);
}

/**
 * `PUT /v2/admin/staff/{id}` → update a staff member. Returns the re-parsed updated
 * row. Throws a typed `ApiError`.
 */
export async function updateStaff(id: string, input: StaffInput): Promise<V2Staff | null> {
  const body = await request<unknown>('PUT', staffPath(id), { json: toStaffBody(input) });
  return parseStaff(body);
}

/**
 * `POST /v2/admin/staff/{id}/deactivate` → soft-deactivate a staff member (§4.3 —
 * never a hard delete). Throws a typed `ApiError`.
 */
export async function deactivateStaff(id: string): Promise<void> {
  await request<unknown>('POST', `${staffPath(id)}/deactivate`);
}

/**
 * `POST /v2/admin/staff/{id}/reactivate` → reactivate a soft-deactivated staff
 * member (§4.3). Throws a typed `ApiError`.
 */
export async function reactivateStaff(id: string): Promise<void> {
  await request<unknown>('POST', `${staffPath(id)}/reactivate`);
}

/**
 * `DELETE /v2/admin/staff/{id}` → Remove (archive) a staff member (DNKAPI-0697 /
 * DNKADM-0481): OWNER/ADMIN only, never a hard delete — their past bookings keep their
 * name. Throws a typed `ApiError`; `409 STAFF_HAS_UPCOMING_BOOKINGS` means NOTHING was
 * removed and carries the bookings in the way (`parseStaffUpcomingBookings`,
 * `@/features/staff/staffError`).
 */
export async function removeStaff(id: string): Promise<void> {
  await request<unknown>('DELETE', staffPath(id));
}

/**
 * `POST /v2/admin/staff/{id}/restore` → bring a removed staff member back (DNKAPI-0697 /
 * DNKADM-0481). They return INACTIVE — reactivating is a separate, plan-limited step.
 * Returns the re-parsed row. Throws a typed `ApiError`.
 */
export async function restoreStaff(id: string): Promise<V2Staff | null> {
  const body = await request<unknown>('POST', `${staffPath(id)}/restore`);
  return parseStaff(body);
}

// --- Location assignments (DNKADM-0053 U1) -------------------------------------

/**
 * `GET /v2/admin/staff/{id}/location-assignments` → the owned `v2_location.id`s the
 * staff member is assigned to (the locations they can be scheduled at). Tolerantly
 * extracts the id array (bare array, or `{ locationIds }`/`{ locationAssignments }`
 * of ids or `{ locationId }` objects). Throws a typed `ApiError`.
 */
export async function getStaffLocations(id: string): Promise<string[]> {
  const body = await request<unknown>('GET', `${staffPath(id)}/location-assignments`);
  let raw: unknown[] = [];
  if (Array.isArray(body)) raw = body;
  else if (isObject(body)) {
    if (Array.isArray(body['locationIds'])) raw = body['locationIds'];
    else if (Array.isArray(body['locationAssignments'])) raw = body['locationAssignments'];
    else if (Array.isArray(body['content'])) raw = body['content'];
  }
  const ids: string[] = [];
  for (const entry of raw) {
    // Accept a bare id (number or string) or an object carrying `locationId`/`id`.
    const id2 =
      asId(entry) ??
      (isObject(entry) ? asId(entry['locationId']) ?? asId(entry['id']) : null);
    if (id2 !== null && !ids.includes(id2)) ids.push(id2);
  }
  return ids;
}

/**
 * `PUT /v2/admin/staff/{id}/location-assignments` ← `{ locationIds }` → set the WHOLE
 * set of locations a staff member is assigned to (the multi-select round-trips the
 * full set). Throws a typed `ApiError`.
 */
export async function setStaffLocations(id: string, locationIds: string[]): Promise<void> {
  await request<unknown>('PUT', `${staffPath(id)}/location-assignments`, {
    json: { locationIds },
  });
}

// --- Weekly schedule per location (DNKADM-0053 U2) -----------------------------

/**
 * `GET /v2/admin/staff/{id}/schedule?locationId=…` → the staff member's weekly
 * schedule AT that location (§5.6 — schedules are per-(staff, location)). Always
 * seven days in `dayOfWeek` 0→6 (Sun→Sat) order; a missing day fills unavailable.
 * Throws a typed `ApiError`.
 */
export async function getStaffSchedule(
  id: string,
  locationId: string,
): Promise<StaffSchedule> {
  const body = await request<unknown>('GET', `${staffPath(id)}/schedule`, {
    query: { locationId },
  });
  return parseSchedule(body);
}

/**
 * `PUT /v2/admin/staff/{id}/schedule?locationId=…` ← `{ days }` → set the WHOLE 7-day
 * multi-shift schedule for the staff member at that location. Client validation
 * (`@/utils/shiftValidation`) should run first. Throws a typed `ApiError` (`400`
 * VALIDATION on a bad shift/overlap, upstream forwarded). Refetch after it resolves.
 */
export async function putStaffSchedule(
  id: string,
  locationId: string,
  days: StaffScheduleDay[],
): Promise<void> {
  await request<unknown>('PUT', `${staffPath(id)}/schedule`, {
    query: { locationId },
    json: { days: days.map(scheduleDayToBody) },
  });
}

/**
 * What a coach's own weekly-hours save returns (DNKADM-0490 / DNKAPI-0708): the saved
 * week, plus the bookings that now fall outside it. The save has ALREADY persisted;
 * `bookingsOutsideHours` is a warning to show, never a refusal. Entries use the
 * `STAFF_CONFLICT` booking shape, soonest first, times as ISO-8601 in the location zone.
 */
export interface MyScheduleSaveResult {
  days: StaffScheduleDay[];
  bookingsOutsideHours: BookingConflict[];
}

/**
 * `PUT /v2/me/staff/schedule?locationId=…` ← `{ days }` → the SIGNED-IN coach saves
 * their OWN weekly hours at one of their assigned locations (DNKADM-0490 /
 * DNKAPI-0708). Same body as {@link putStaffSchedule}; there is no staff id on the
 * wire — the server resolves "which coach" from the session, so this call can never
 * name another coach. Refusals the caller maps to plain copy: `403
 * NOT_OWN_STAFF_RECORD` / `COACH_SELF_SCHEDULE_DISABLED` / `STAFF_LOCATION_NOT_ASSIGNED`
 * / `LOCATION_ACCESS_DENIED`, and the admin write's `400` shift codes. Throws a typed
 * `ApiError`.
 */
export async function putMyStaffSchedule(
  locationId: string,
  days: StaffScheduleDay[],
): Promise<MyScheduleSaveResult> {
  const body = await request<unknown>('PUT', `${ME_STAFF_PATH}/schedule`, {
    query: { locationId },
    json: { days: days.map(scheduleDayToBody) },
  });
  const o = isObject(body) ? body : {};
  const raw = o['bookingsOutsideHours'];
  const bookingsOutsideHours: BookingConflict[] = [];
  if (Array.isArray(raw)) {
    for (const entry of raw) {
      const parsed = parseBookingConflict(entry);
      if (parsed !== null) bookingsOutsideHours.push(parsed);
    }
  }
  return { days: parseScheduleDays(o['days']), bookingsOutsideHours };
}

// --- Date exceptions (DNKADM-0053 U2) ------------------------------------------

/**
 * `GET /v2/admin/staff/{id}/date-exceptions` → the staff member's date exceptions
 * (defensively parsed; a bad entry dropped). Throws a typed `ApiError`.
 */
export async function listStaffDateExceptions(id: string): Promise<StaffDateException[]> {
  const body = await request<unknown>('GET', `${staffPath(id)}/date-exceptions`);
  return extractList(body, 'exceptions')
    .map(parseException)
    .filter((e): e is StaffDateException => e !== null);
}

/**
 * `POST /v2/admin/staff/{id}/date-exceptions` ← one exception → the created row
 * (re-parsed). Client validation (`@/utils/shiftValidation`) should run on its shifts
 * first. Throws a typed `ApiError`. Refetch after it resolves.
 */
export async function createStaffDateException(
  id: string,
  input: StaffDateExceptionInput,
): Promise<StaffDateException | null> {
  const body = await request<unknown>('POST', `${staffPath(id)}/date-exceptions`, {
    json: exceptionToBody(input),
  });
  return parseException(body);
}

/**
 * `PUT /v2/admin/staff/{id}/date-exceptions/{exceptionId}` ← the same body as the create
 * → the updated exception (DNKADM-0388 U3: a second block window on a date that already
 * has an OPEN exception updates it in place). Throws a typed `ApiError`.
 */
export async function updateStaffDateException(
  id: string,
  exceptionId: string,
  input: StaffDateExceptionInput,
): Promise<StaffDateException | null> {
  const body = await request<unknown>(
    'PUT',
    `${staffPath(id)}/date-exceptions/${encodeURIComponent(exceptionId)}`,
    { json: exceptionToBody(input) },
  );
  return parseException(body);
}

/**
 * `DELETE /v2/admin/staff/{id}/date-exceptions/{exceptionId}` → remove one date
 * exception. Throws a typed `ApiError`. Refetch after it resolves.
 */
export async function deleteStaffDateException(
  id: string,
  exceptionId: string,
): Promise<void> {
  await request<unknown>(
    'DELETE',
    `${staffPath(id)}/date-exceptions/${encodeURIComponent(exceptionId)}`,
  );
}

// --- Appointment caps (DNKADM-0053 U2) -----------------------------------------

/**
 * `GET /v2/admin/staff/{id}/appointment-caps` → the staff member's appointment caps
 * (at most one per period; defensively parsed). An under-specified `countedStatuses`
 * degrades to all six (§5.6 default). Throws a typed `ApiError`.
 */
export async function listStaffCaps(id: string): Promise<StaffAppointmentCap[]> {
  const body = await request<unknown>('GET', `${staffPath(id)}/appointment-caps`);
  return extractList(body, 'appointmentCaps')
    .map(parseCap)
    .filter((c): c is StaffAppointmentCap => c !== null);
}

/**
 * `PUT /v2/admin/staff/{id}/appointment-caps/{period}` ← `{ maxAppointments,
 * countedStatuses }` → upsert the cap for that period (at most one per `(staff,
 * period)`, §5.6). Returns the re-parsed cap. Throws a typed `ApiError`.
 */
export async function upsertStaffCap(
  id: string,
  period: CapPeriod,
  input: StaffAppointmentCapInput,
): Promise<StaffAppointmentCap | null> {
  const body = await request<unknown>(
    'PUT',
    `${staffPath(id)}/appointment-caps/${encodeURIComponent(period)}`,
    { json: { maxAppointments: input.maxAppointments, countedStatuses: input.countedStatuses } },
  );
  return parseCap(body);
}

/**
 * `DELETE /v2/admin/staff/{id}/appointment-caps/{period}` → remove the cap for that
 * period (no cap = uncapped). Throws a typed `ApiError`.
 */
export async function deleteStaffCap(id: string, period: CapPeriod): Promise<void> {
  await request<unknown>(
    'DELETE',
    `${staffPath(id)}/appointment-caps/${encodeURIComponent(period)}`,
  );
}

// --- The shared, fail-closed staff pick-list (DNKADM-0053 U1) -------------------

/**
 * Load the ACTIVE staff as `{ id, name }` options, **fail-closed**: any
 * upstream/network failure OR a malformed (non-array) `200` body throws the shared
 * {@link PickListError}, so the loader renders error + Retry and NEVER a silent empty
 * list (a failure can never masquerade as "loaded, empty"). A successful empty list
 * returns `[]` (a real first-run state). `name` is `"firstName lastName"` (trimmed),
 * falling back to whichever part is present, then the id. Only `active` staff are
 * offered (a deactivated staffer must not be selectable on a service / as a coach).
 *
 * Mirrors `getServiceCategoryOptions` / the `@/api/picklists` fail-closed wrappers,
 * reusing their `PickListError` so `usePickList`'s `failClosed` detection (and the
 * loader copy) work unchanged. Consumed by the service form (0055) + coach
 * assignment (0059) via {@link useStaffPickList}.
 *
 * `includeInactive` (opt-in; default `false`) broadens the fetch to `status=all` and
 * carries each staffer's `isActive` through — used only by the service-detail
 * eligible-staff editor (DNKADM-0134), so an already-assigned but since-deactivated
 * staffer stays visible/editable. The no-args default preserves the active-only
 * behavior `useStaffPickList()`/`CoachAssignmentControl` rely on.
 */
export async function getStaffOptions(
  options: { includeInactive?: boolean } = {},
): Promise<StaffOption[]> {
  try {
    const status = options.includeInactive ? 'all' : 'active';
    const body = await request<unknown>('GET', PATH, { query: { status } });
    const list = extractStaff(body);
    // A non-array success cannot be trusted as a COMPLETE list — fail-closed.
    if (list === null) throw new Error('expected an array of staff');
    return list
      .map(parseStaff)
      .filter((s): s is V2Staff => s !== null)
      .map((s) => ({ id: s.id, name: staffDisplayName(s), isActive: s.isActive }));
  } catch (cause) {
    throw new PickListError('staff', cause);
  }
}

/** `"firstName lastName"` (trimmed), falling back to either part, then the id. */
export function staffDisplayName(staff: Pick<V2Staff, 'id' | 'firstName' | 'lastName'>): string {
  const name = `${staff.firstName} ${staff.lastName}`.trim();
  return name.length > 0 ? name : staff.id;
}

/**
 * `useStaffPickList()` — the fail-closed staff selector the service form (0055) +
 * coach assignment (0059) import.
 *
 * Loads live via {@link getStaffOptions} through `usePickList` (fail-closed):
 * `loading → ready` with the `{ id, name }[]` options, or `loading → error + Retry` —
 * never a partial/empty-on-failure list. Render through `<PickListLoader>`
 * (`@/components/PickListLoader`). Mirrors the service detail's
 * `getServiceCategoryOptions` through `usePickList` path.
 */
export function useStaffPickList(): PickList<StaffOption> {
  return usePickList(useCallback(() => getStaffOptions(), []), []);
}
